import { z } from "zod";
import { userIdSchema } from "./identity.js";
export const actionButtonSchema = z.object({
    id: z.string().min(1).max(50),
    text: z.string().min(1).max(30),
});
export const notificationSchema = z.object({
    userId: userIdSchema,
    title: z.string(),
    body: z.string(),
    data: z.record(z.string(), z.unknown()).default({}),
    deepLink: z.string().nullable().default(null),
    // Push action buttons (OneSignal). Senders that don't support them ignore these.
    actionButtons: z.array(actionButtonSchema).optional(),
    // iOS notification category id, must be pre-registered on the device.
    iosCategory: z.string().nullable().optional(),
    // Only these delivery channels (web-push, onesignal, slack, msteams); absent means all of them.
    channels: z.array(z.string()).optional(),
});
/**
 * The active slot a notification occupies in a member's priority list.
 *
 * A notification feed is a view of what is current, not an archive. A fresh
 * reminder therefore supersedes the old reminder for the same thing. Moments
 * retain their rule in the key, so a newer desk-break nudge does not replace a
 * different active decision.
 */
export function notificationSupersessionKey(input) {
    const data = input.data;
    const kind = stringData(data?.kind);
    if (kind === "moment") {
        const rule = stringData(data?.rule) ?? stringData(data?.action);
        return rule ? `moment:${rule}` : "kind:moment";
    }
    if (kind === "connection.reauth_required") {
        const provider = stringData(data?.provider);
        return provider ? `kind:${kind}:${provider}` : `kind:${kind}`;
    }
    if (kind)
        return `kind:${kind}`;
    const action = stringData(data?.action);
    return action ? `action:${action}` : null;
}
export const notificationRowSchema = notificationSchema.extend({
    id: z.string(),
    createdAt: z.coerce.date(),
    dismissedAt: z.coerce.date().nullable(),
    // Grouping columns (nullable on rows that never participated in grouping).
    // Producers pass these in `data` (see @healthos/notifications dedupe.ts);
    // the persistence layer copies them onto the dedicated DB columns so
    // `findUnreadByGroupKey` is indexable on `group_key` rather than probing
    // JSONB. `actorIds` is stored as a JSONB array of user UUID strings at
    // the DB layer; the row schema returns it as `string[]` when present.
    groupKey: z.string().nullable().default(null),
    actorIds: z.array(z.string()).nullable().default(null),
    actorCount: z.number().int().nonnegative().default(1),
    firstActorName: z.string().nullable().default(null),
});
function stringData(value) {
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
//# sourceMappingURL=notifications.js.map